/**
 * Hex Barrel Builder JS
 * ---------------------------------------------------------------------------
 * Drives the layered "build your own barrel" experience: swatch selection
 * per slot, live stock/quantity capping, layered transparent-PNG preview
 * compositing, a color-matched "included" thumbnail alongside the canvas,
 * and an add to cart with a shared _build_id property so items group
 * visually in cart.
 *
 * Adapted from tank-builder-0722.js. On purpose, this version does NOT
 * carry over:
 *   - Addon questions and Optional add-on (upsell) blocks -- Hex Barrel
 *     has three slots and nothing else.
 *   - Auto-pre-selecting a default variant per slot on load -- every slot
 *     starts unselected here, since all three are required and the
 *     customer should deliberately choose each color.
 *   - initStickyPreviewCrop -- replaced by initHeroRotate, a scroll-linked
 *     rotate + scale instead of a crop + zoom, running on both desktop and
 *     mobile instead of mobile only.
 *
 * New in this version:
 *   - updateGroupImage: any slot block with a "group_image_label" setting
 *     filled in (Insert, in the current build) also updates a second,
 *     non-canvas thumbnail in the included row from that variant's
 *     builder.inserts_group_image metafield, the moment a color is picked.
 *   - Native Shopify compare_at_price is read straight off each variant
 *     for sale pricing (strikethrough original + sale price), both in the
 *     full-page picker rows (rendered directly in Liquid) and in the
 *     collapsed current-selection card (built here in JS).
 *
 * Works against the markup emitted by sections/hex-barrel-builder.liquid.
 * Multiple builder instances can exist on a page; each is namespaced by
 * its section id (passed into HexBarrelBuilder.init).
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var instances = {};

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function priceHtml(cents, compareAtCents) {
    if (compareAtCents && compareAtCents > cents) {
      return '<span class="hb-price-sale-original">' + formatMoney(compareAtCents) + '</span>' +
        '<span class="hb-price-sale-current">' + formatMoney(cents) + '</span>';
    }
    return formatMoney(cents);
  }

  function Builder(rootId) {
    this.root = document.getElementById(rootId);
    if (!this.root) return;
    this.sectionId = this.root.dataset.sectionId;
    this.state = {
      slots: {} // slotKey -> { variantId, price, compareAtPrice, available, inventoryQty, inventoryPolicy, title }
    };
    this.slotEls = qsa(this.root, '.hb-slot');
    this.bindEvents();
    this.updateSummary();
    if (this.root.dataset.randomPreview === 'true') {
      this.showRandomPreview();
    }
  }

  Builder.prototype.bindEvents = function () {
    var self = this;

    qsa(this.root, '.hb-row').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        self.selectSwatch(btn);
      });
    });

    qsa(this.root, '.hb-current-selection').forEach(function (card) {
      card.addEventListener('click', function () {
        var slotEl = card.closest('.hb-slot');
        if (slotEl.dataset.slotSoldOut === 'true') return;
        self.openModal(slotEl.dataset.slotKey);
      });
    });

    qsa(this.root, '.hb-select-modal-close').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.closeModal(btn.dataset.slotKey);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.activeModalSlotKey) {
        self.closeModal(self.activeModalSlotKey);
      }
    });

    var addBtn = qs(this.root, '.hb-add-to-cart');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        self.addToCart();
      });
    }
  };

  Builder.prototype.openModal = function (slotKey) {
    var modal = document.getElementById('hb-modal-' + this.sectionId + '-' + slotKey);
    if (!modal) return;
    modal.hidden = false;
    document.body.classList.add('hb-modal-open');
    this.activeModalSlotKey = slotKey;
  };

  Builder.prototype.closeModal = function (slotKey) {
    var modal = document.getElementById('hb-modal-' + this.sectionId + '-' + slotKey);
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('hb-modal-open');
    this.activeModalSlotKey = null;
  };

  function swapThumbToImage(thumbEl, src) {
    if (!thumbEl) return thumbEl;
    if (thumbEl.tagName === 'IMG') {
      thumbEl.src = src;
      thumbEl.hidden = false;
      return thumbEl;
    }
    var img = document.createElement('img');
    img.className = 'hb-current-thumb';
    img.id = thumbEl.id;
    img.src = src;
    img.alt = '';
    thumbEl.replaceWith(img);
    return img;
  }

  /**
   * Updates the collapsed "current selection" card (thumbnail, title,
   * price, required indicator) to reflect whatever was just chosen.
   */
  Builder.prototype.updateCurrentSelectionCard = function (slotKey, btn) {
    var thumbEl = document.getElementById('hb-current-thumb-' + this.sectionId + '-' + slotKey);
    var titleEl = document.getElementById('hb-current-title-' + this.sectionId + '-' + slotKey);
    var priceEl = document.getElementById('hb-current-price-' + this.sectionId + '-' + slotKey);
    var requiredEl = document.getElementById('hb-required-' + this.sectionId + '-' + slotKey);
    if (!titleEl || !priceEl) return;

    titleEl.textContent = btn.dataset.variantTitle === 'Default Title'
      ? btn.dataset.productTitle
      : btn.dataset.productTitle + ' - ' + btn.dataset.variantTitle;
    titleEl.classList.remove('hb-current-title-empty');

    var cents = parseInt(btn.dataset.price, 10);
    var compareAtCents = btn.dataset.compareAtPrice ? parseInt(btn.dataset.compareAtPrice, 10) : null;
    priceEl.innerHTML = priceHtml(cents, compareAtCents);

    if (btn.dataset.swatchImage) {
      swapThumbToImage(thumbEl, btn.dataset.swatchImage);
    }

    if (requiredEl) {
      requiredEl.textContent = 'Selected';
      requiredEl.classList.add('hb-required-met');
    }
  };

  Builder.prototype.applySwatchSelection = function (slotKey, btn) {
    var qtyAvailable = btn.dataset.inventoryPolicy === 'continue'
      ? Infinity
      : parseInt(btn.dataset.inventoryQty, 10);

    this.state.slots[slotKey] = {
      variantId: btn.dataset.variantId,
      price: parseInt(btn.dataset.price, 10),
      compareAtPrice: btn.dataset.compareAtPrice ? parseInt(btn.dataset.compareAtPrice, 10) : null,
      available: btn.dataset.available === 'true',
      inventoryQty: qtyAvailable,
      qty: 1,
      title: btn.dataset.variantTitle === 'Default Title'
        ? btn.dataset.productTitle
        : btn.dataset.productTitle + ' - ' + btn.dataset.variantTitle,
      builderImage: btn.dataset.builderImage
    };

    this.updateLayer(slotKey, btn.dataset.builderImage);
    this.updateGroupImage(slotKey, btn);
    this.updateCurrentSelectionCard(slotKey, btn);
  };

  Builder.prototype.selectSwatch = function (btn) {
    var slotKey = btn.dataset.slotKey;

    var grid = document.getElementById('hb-grid-' + this.sectionId + '-' + slotKey);
    qsa(grid, '.hb-row').forEach(function (b) {
      b.classList.remove('hb-row-selected');
    });
    btn.classList.add('hb-row-selected');

    this.applySwatchSelection(slotKey, btn);
    this.closeModal(slotKey);
    this.updateSummary();
  };

  Builder.prototype.updateLayer = function (slotKey, imageUrl) {
    var img = document.getElementById('hb-layer-' + this.sectionId + '-' + slotKey);
    if (!img) return;
    var self = this;
    var hadImage = !img.hidden && !!img.src;

    function apply() {
      if (imageUrl) {
        img.src = imageUrl;
        img.hidden = false;
        img.style.opacity = '0';
        // Next frame, so the browser registers opacity:0 as the actual
        // starting point before animating to 1 -- setting both in the
        // same frame would just skip straight to the end state.
        requestAnimationFrame(function () {
          img.style.opacity = '1';
        });
        self.triggerSheen(imageUrl);
      } else {
        img.src = '';
        img.hidden = true;
        img.style.opacity = '1';
      }
      self.updatePlaceholderVisibility();
    }

    if (hadImage) {
      // Fade the current one out first, matching .hb-layer's CSS
      // transition duration, then swap and fade the new one in --
      // this is the actual crossfade; swapping src directly doesn't
      // animate anything, since opacity never changes on its own.
      img.style.opacity = '0';
      setTimeout(apply, 180);
    } else {
      apply();
    }
  };

  /**
   * Sweeps a soft diagonal highlight, masked to whichever single layer
   * just changed -- see the mask-image comment on .hb-sheen in the CSS
   * for why a mask is what makes this possible. All three parts share
   * the exact same square canvas rather than separate regions, so
   * there's no rectangular area to confine this to any other way; the
   * mask clips the shine down to that one layer's actual silhouette
   * using the same image, so it only ever plays over that part.
   */
  Builder.prototype.triggerSheen = function (layerImageUrl) {
    var sheen = document.getElementById('hb-sheen-' + this.sectionId);
    if (!sheen || !layerImageUrl) return;
    sheen.style.maskImage = 'url(' + layerImageUrl + ')';
    sheen.style.webkitMaskImage = 'url(' + layerImageUrl + ')';
    sheen.style.transition = 'none';
    sheen.style.backgroundPosition = '-150% 0';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        sheen.style.transition = 'background-position 1100ms ease';
        sheen.style.backgroundPosition = '150% 0';
      });
    });
  };

  /**
   * Only does anything for a slot block whose "group_image_label" setting
   * was filled in (Liquid only renders a #hb-group-img-* element for those
   * slots), so this is a safe no-op on every other slot -- no separate
   * flag needed here to know which slot is which.
   */
  Builder.prototype.updateGroupImage = function (slotKey, btn) {
    var img = document.getElementById('hb-group-img-' + this.sectionId + '-' + slotKey);
    var empty = document.getElementById('hb-group-empty-' + this.sectionId + '-' + slotKey);
    if (!img) return;
    var url = btn.dataset.groupImage;
    if (url) {
      img.src = url;
      img.hidden = false;
      if (empty) empty.hidden = true;
    } else {
      img.src = '';
      img.hidden = true;
      if (empty) empty.hidden = false;
    }
  };

  Builder.prototype.updatePlaceholderVisibility = function () {
    var placeholder = document.getElementById('hb-placeholder-' + this.sectionId);
    if (!placeholder) return;
    var anyVisible = qsa(this.root, '.hb-layer').some(function (img) {
      return !img.hidden;
    });
    placeholder.hidden = anyVisible;
  };

  /**
   * Fills the canvas with a random color per slot on load, purely so
   * there's a fully built barrel on screen instead of empty layers --
   * NOT a real selection. Deliberately does not touch this.state.slots,
   * the current-selection cards, or the required indicators, and Add to
   * Cart stays disabled until the customer actually picks each color
   * themselves. Skips any slot with no available rows (e.g. currently
   * sold out) rather than forcing a choice.
   */
  Builder.prototype.showRandomPreview = function () {
    var self = this;
    this.slotEls.forEach(function (slotEl) {
      var slotKey = slotEl.dataset.slotKey;
      var grid = document.getElementById('hb-grid-' + self.sectionId + '-' + slotKey);
      if (!grid) return;
      var rows = qsa(grid, '.hb-row');
      if (rows.length === 0) return;
      var pick = rows[Math.floor(Math.random() * rows.length)];
      self.updateLayer(slotKey, pick.dataset.builderImage);
      self.updateGroupImage(slotKey, pick);
    });
  };

  Builder.prototype.updateSummary = function () {
    var total = 0;
    var filledCount = 0;
    var requiredCount = this.slotEls.length;

    for (var key in this.state.slots) {
      var slot = this.state.slots[key];
      if (slot) {
        filledCount++;
        total += slot.price * (slot.qty || 1);
      }
    }

    var subtotalEl = document.getElementById('hb-subtotal-' + this.sectionId);
    if (subtotalEl) subtotalEl.textContent = formatMoney(total);

    var addBtn = qs(this.root, '.hb-add-to-cart');
    if (addBtn) addBtn.disabled = filledCount < requiredCount;
  };

  Builder.prototype.showError = function (msg) {
    var el = document.getElementById('hb-error-' + this.sectionId);
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 5000);
  };

  Builder.prototype.showSuccess = function () {
    var el = document.getElementById('hb-success-' + this.sectionId);
    if (!el) return;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 4000);
  };

  Builder.prototype.getCartSectionIdsToRender = function () {
    var ids = [];
    var cartItemsEl = document.getElementById('cart-items');
    if (cartItemsEl && cartItemsEl.dataset.section) {
      ids.push(cartItemsEl.dataset.section);
    } else {
      ids.push('cart-drawer');
    }
    ids.push('cart-icon-bubble');
    return ids;
  };

  Builder.prototype.renderCartDrawerAndOpen = function (parsedState) {
    var renderedSomething = false;

    if (parsedState && parsedState.sections && parsedState.sections['cart-drawer']) {
      try {
        var html = parsedState.sections['cart-drawer'];
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var sourceCartItems = parsed.getElementById('cart-items');
        var targetCartItems = document.getElementById('cart-items');

        if (sourceCartItems && targetCartItems) {
          targetCartItems.innerHTML = sourceCartItems.innerHTML;
          targetCartItems.dataset.empty = sourceCartItems.dataset.empty || 'false';
          renderedSomething = true;
        }

        var sourceBubble = parsed.getElementById('cart-icon-bubble');
        var targetBubble = document.getElementById('cart-icon-bubble');
        if (sourceBubble && targetBubble) {
          targetBubble.innerHTML = sourceBubble.innerHTML;
        }

        var sourceSummary = parsed.querySelector('.cart-drawer__summary');
        var targetSummary = document.querySelector('.cart-drawer__summary');
        if (sourceSummary && targetSummary) {
          targetSummary.innerHTML = sourceSummary.innerHTML;
        }
      } catch (e) {
        console.error('[HexBarrelBuilder] Manual cart-items update failed:', e);
      }
    }

    this.openCartDrawer();
    if (!renderedSomething) {
      // #cart-items wasn't found in either this page or the returned
      // section HTML -- same limitation as Tank Builder, not attempted
      // to fix here since it's a theme-wide cart drawer concern.
    }
  };

  Builder.prototype.openCartDrawer = function () {
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('cart:show'));

    var drawerSelectors = [
      'cart-drawer',
      '#CartDrawer',
      '[data-cart-drawer]',
      '.cart-drawer',
      '#cart-drawer',
      '#sidebar-cart',
      '#mini-cart'
    ];
    var drawer = null;
    for (var i = 0; i < drawerSelectors.length; i++) {
      var el = document.querySelector(drawerSelectors[i]);
      if (el) { drawer = el; break; }
    }

    if (drawer) {
      if (typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      if (typeof drawer.show === 'function') {
        drawer.show();
        return;
      }
      drawer.classList.add('open', 'active', 'is-open', 'drawer--open');
      drawer.setAttribute('open', '');
      drawer.removeAttribute('hidden');
      return;
    }

    var triggerSelectors = [
      '[data-cart-icon]',
      '#cart-icon-bubble',
      '.header__icon--cart',
      '[data-drawer-trigger="cart"]',
      'button[aria-controls*="cart" i]',
      'a[href="/cart"][data-action]'
    ];
    for (var j = 0; j < triggerSelectors.length; j++) {
      var trigger = document.querySelector(triggerSelectors[j]);
      if (trigger) {
        trigger.click();
        return;
      }
    }

    window.location.href = '/cart';
  };

  Builder.prototype.addToCart = function () {
    var self = this;

    var missing = this.slotEls.filter(function (slotEl) {
      return !self.state.slots[slotEl.dataset.slotKey];
    });
    if (missing.length > 0) {
      this.showError('Select a color for each part first.');
      missing[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var items = [];
    var buildId = 'build_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    var badStock = false;

    for (var key in this.state.slots) {
      var slot = this.state.slots[key];
      var qty = slot.qty || 1;

      if (qty > slot.inventoryQty) {
        this.showError('Not enough stock for ' + slot.title + '.');
        badStock = true;
        break;
      }

      items.push({
        id: slot.variantId,
        quantity: qty,
        properties: { '_build_id': buildId, '_build_slot': key }
      });
    }
    if (badStock) return;

    var addBtn = qs(this.root, '.hb-add-to-cart');
    addBtn.disabled = true;
    addBtn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: self.getCartSectionIdsToRender(),
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw new Error(err.description || 'Could not add to cart.');
          });
        }
        return res.json();
      })
      .then(function (parsedState) {
        self.showSuccess();
        addBtn.textContent = 'Add To Cart';
        addBtn.disabled = false;
        self.renderCartDrawerAndOpen(parsedState);
      })
      .catch(function (err) {
        self.showError(err.message || 'Something went wrong adding to cart.');
        addBtn.textContent = 'Add To Cart';
        addBtn.disabled = false;
      });
  };

  /**
   * Scroll-linked rotate + scale hero, targeting a dedicated
   * .hb-showcase area below the builder block rather than the full
   * browser window. Three phases, driven by comparing window.scrollY
   * against measured thresholds:
   *
   *   "before" - above the section. .hb-preview-fixed sits in normal
   *              document flow, inside .hb-preview-col, at its small
   *              resting size.
   *   "during" - scrolled into the transition zone. .hb-preview-fixed
   *              switches to position: fixed and interpolates from the
   *              builder column's own position/width to the showcase
   *              area's measured position/width, rotating + scaling
   *              along the way.
   *   "after"  - past the transition zone. .hb-preview-fixed is
   *              re-parented into .hb-showcase itself and rejoins
   *              normal document flow there, fully contained, sized to
   *              fit, and the page continues normally beneath it.
   *
   * The transition zone's length is DERIVED from the actual distance
   * between the builder block and the showcase area (measured, not a
   * hand-picked constant), so it stays correct however tall the color
   * picker ends up being rather than needing to be re-tuned by hand.
   *
   * Fixed positioning is used for "during" specifically because it's
   * calculated purely relative to the browser window -- it doesn't care
   * what grid it's in or what any ancestor's overflow is set to, which
   * earlier versions relying on position: sticky kept getting tripped
   * up by.
   */
  function initHeroRotate(root) {
    var previewCol = root.querySelector('.hb-preview-col');
    var fixedEl = root.querySelector('.hb-preview-fixed');
    var canvas = root.querySelector('.hb-canvas');
    var includedRow = root.querySelector('.hb-included-row');
    var showcase = root.querySelector('.hb-showcase');
    if (!previewCol || !fixedEl || !canvas || !showcase) return;

    var originalParent = fixedEl.parentNode;

    // Starting angle/scale match the small resting build view. These
    // rotation values are added ON TOP of whatever angle is already
    // baked into the artwork itself. The numbers below are YOUR live-
    // tuned values from testing against the real art, not the original
    // starting guesses -- the original assumption was a simple "45deg
    // one way to flatten, 45deg the other way toward vertical," but
    // your actual numbers (particularly END_ANGLE_DESKTOP) go well
    // beyond that, which just means the real art or the look you wanted
    // needed more rotation than that first guess. Keep tuning these
    // directly; there's no fixed relationship they need to preserve.
    //
    // END_SCALE governs how large the settled canvas ends up relative
    // to the showcase it lands in -- watch for it spilling past the
    // showcase's edges if you push this much higher.
    var START_ANGLE = 10;
    var END_ANGLE_DESKTOP = -135;
    var END_ANGLE_MOBILE = -45;
    var START_SCALE = 1.35;
    var END_SCALE = 1.55;
    var CANVAS_BASE_RATIO = 0.55; // matches .hb-canvas's width in the CSS
    // Confirmed: the assembled barrel runs corner to corner across the
    // 2400x2400 canvas at 45deg, with about 10% empty margin at each
    // end. BARREL_THICKNESS_RATIO (how thick the barrel is along its
    // SHORT axis, as a fraction of the canvas's side) was NOT confirmed
    // -- 0.16 is an estimate for a fairly slim tube shape. This is the
    // one number in this block worth double-checking against the real
    // art; everything else below is derived from the confirmed 10%.
    var CORNER_MARGIN_RATIO = 0.10;
    var BARREL_THICKNESS_RATIO = 0.32;
    // Length of the drawn barrel relative to the canvas's own side --
    // the diagonal (side * root 2) minus the margin at each end.
    var BARREL_LENGTH_RATIO = Math.SQRT2 * (1 - 2 * CORNER_MARGIN_RATIO);

    // The barrel's bounding height at any point in the rotation, not
    // just interpolated between the resting and settled endpoints.
    // This matters because the real geometry isn't linear: the barrel
    // is at its full 45deg tilt at rest and only really flattens out in
    // the last few degrees of rotation, so a straight-line interpolation
    // between the two endpoint heights shrinks the container well
    // before the image has actually finished flattening -- which is
    // exactly what was clipping it right before it reached horizontal.
    // phiDeg is the barrel's current angle from horizontal: starts at
    // the baseline 45deg tilt and moves toward 0 (desktop, landing
    // flat) or 90 (mobile, landing vertical) as progress goes 0 to 1.
    function neededHeightAt(containerWidthPx, progress) {
      var isMobile = mobileQuery.matches;
      var scale = START_SCALE + (END_SCALE - START_SCALE) * progress;
      var canvasSide = containerWidthPx * CANVAS_BASE_RATIO * scale;
      var phiDeg = isMobile ? (45 + 45 * progress) : (45 - 45 * progress);
      var phiRad = phiDeg * Math.PI / 180;
      return canvasSide * (
        BARREL_LENGTH_RATIO * Math.abs(Math.sin(phiRad)) +
        BARREL_THICKNESS_RATIO * Math.abs(Math.cos(phiRad))
      );
    }

    var mobileQuery = window.matchMedia('(max-width: 860px)');
    var sectionTop = 0;
    var showcaseDocTop = 0;
    var colLeft = 0;
    var colWidth = 0;
    var showcaseLeft = 0;
    var showcaseWidth = 0;
    var showcaseHeightPx = 0;
    var lastPhase = null;
    var ticking = false;

    // Reads layout (getBoundingClientRect) -- only ever called from
    // page load and resize, never from the scroll handler, so it can't
    // fight with the writes applyTransform/setPhase do on every frame.
    function measure() {
      if (fixedEl.parentNode !== originalParent) {
        originalParent.appendChild(fixedEl);
        fixedEl.style.position = '';
        fixedEl.style.top = '';
        fixedEl.style.left = '';
        fixedEl.style.width = '';
        fixedEl.style.height = '';
      }

      var colRect = previewCol.getBoundingClientRect();
      sectionTop = colRect.top + window.scrollY;
      colLeft = colRect.left;
      colWidth = colRect.width;
      // Locks in previewCol's natural resting height permanently, so
      // it can't collapse once fixedEl (its only child) goes
      // position: fixed during "during" and is pulled out of normal
      // flow. Without this, the whole page above the showcase would
      // shift upward the instant the effect starts, since nothing was
      // holding that space open -- which was shifting the showcase
      // itself out from under where the scroll math expected it to be.
      previewCol.style.minHeight = colRect.height + 'px';

      var showRect = showcase.getBoundingClientRect();
      showcaseDocTop = showRect.top + window.scrollY;
      showcaseLeft = showRect.left;
      showcaseWidth = showRect.width;

      // Same formula "during" uses at progress 1, so the showcase's own
      // CSS height always matches exactly what the floating image
      // converges to right before landing in it.
      showcaseHeightPx = neededHeightAt(showcaseWidth, 1) + 48;
      showcase.style.height = showcaseHeightPx + 'px';

      lastPhase = null; // force setPhase to re-apply after a remeasure
    }

    function applyTransform(progress) {
      var isMobile = mobileQuery.matches;
      var endAngle = isMobile ? END_ANGLE_MOBILE : END_ANGLE_DESKTOP;
      var angle = START_ANGLE + (endAngle - START_ANGLE) * progress;
      var scale = START_SCALE + (END_SCALE - START_SCALE) * progress;
      canvas.style.transform = 'rotate(' + angle.toFixed(2) + 'deg) scale(' + scale.toFixed(3) + ')';

      if (includedRow) {
        var fade = Math.max(0, 1 - progress / 0.25);
        includedRow.style.opacity = fade.toFixed(2);
        includedRow.style.pointerEvents = fade > 0.05 ? 'auto' : 'none';
        // display:none once fully faded (not just invisible) so it
        // stops taking up space in the flex column -- otherwise the
        // height formula above, which only accounts for the canvas
        // itself, undercounts what the container actually needs.
        includedRow.style.display = fade > 0.02 ? '' : 'none';
      }
    }

    function setPhase(phase, progress) {
      if (phase === 'during') {
        if (fixedEl.parentNode !== originalParent) {
          originalParent.appendChild(fixedEl);
        }
        // Interpolate from the builder column's own position/width (at
        // progress 0, so there's no jump when this kicks in) to the
        // showcase area's measured position/width (at progress 1, so
        // handing off to the showcase in the "after" branch below is
        // seamless too).
        var targetLeft = colLeft + (showcaseLeft - colLeft) * progress;
        var targetWidth = colWidth + (showcaseWidth - colWidth) * progress;
        fixedEl.style.position = 'fixed';
        fixedEl.style.top = '0px';
        fixedEl.style.left = targetLeft + 'px';
        fixedEl.style.width = targetWidth + 'px';
        // Computed fresh at this exact progress, not interpolated in a
        // straight line between the resting and settled heights. The
        // real geometry isn't linear -- the barrel stays close to its
        // full tilted height for most of the rotation and only really
        // flattens out in the last few degrees, so a straight-line
        // interpolation shrinks the container too early relative to how
        // tilted the image still actually is. That's what was clipping
        // it right before it reached horizontal. This also naturally
        // reaches showcaseHeightPx exactly at progress 1, so the handoff
        // into "after" still lands cleanly.
        fixedEl.style.height = (neededHeightAt(targetWidth, progress) + 48) + 'px';
        applyTransform(progress);
      } else if (phase === 'after') {
        // Moves the actual element into the showcase and lets it rejoin
        // normal document flow there -- fully contained by definition,
        // since the showcase was just sized to fit it, and it scrolls
        // away naturally with the rest of the page from here instead of
        // staying pinned to the viewport forever.
        if (fixedEl.parentNode !== showcase) {
          showcase.appendChild(fixedEl);
        }
        fixedEl.style.position = '';
        fixedEl.style.top = '';
        fixedEl.style.left = '';
        fixedEl.style.width = '';
        fixedEl.style.height = '';
        applyTransform(1);
      } else {
        if (fixedEl.parentNode !== originalParent) {
          originalParent.appendChild(fixedEl);
        }
        fixedEl.style.position = '';
        fixedEl.style.top = '';
        fixedEl.style.left = '';
        fixedEl.style.width = '';
        fixedEl.style.height = '';
        applyTransform(0);
      }
      lastPhase = phase;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;

        // Live check instead of a cached scrollY comparison. The
        // column's real position relative to the viewport can never
        // go stale, no matter what shifted the page's height after
        // load. It also can't fire while the breadcrumb is still on
        // screen, since the breadcrumb sits above this column in the
        // page, it has to have already scrolled out of view before
        // this column's own top can reach 0.
        var liveTop = previewCol.getBoundingClientRect().top;
        if (liveTop > 0) {
          setPhase('before', 0);
          return;
        }

        var start = sectionTop;
        var end = showcaseDocTop;
        var distance = Math.max(1, end - start);
        var y = window.scrollY;

        if (y < end) {
          setPhase('during', Math.min(1, Math.max(0, (y - start) / distance)));
        } else {
          setPhase('after', 1);
        }
      });
    }

    function onResize() {
      measure();
      onScroll();
    }

    measure();
    setPhase('before', 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
  }

  window.HexBarrelBuilder = {
    init: function (rootId) {
      if (instances[rootId]) return instances[rootId];
      var instance = new Builder(rootId);
      instances[rootId] = instance;
      initHeroRotate(instance.root);
      return instance;
    }
  };
})();